import {
  Autocomplete,
  Box,
  InputAdornment,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { t } from 'i18next';
import { useReportFoldersTableFilters } from '@views/pages/Reporting/ReportFolders/hooks';
import { ChangeEvent, ReactNode, useMemo } from 'react';
import { TOOLBAR_FIELD_GAP, toolbarFieldSx } from 'ui';

interface ReportFoldersTableFiltersProps {
  endSlot?: ReactNode;
}

export const ReportFoldersTableFilters = ({
  endSlot,
}: ReportFoldersTableFiltersProps) => {
  const {
    handleSearchChange,
    org,
    handleSelectOrg,
    organizationOptions,
    filters,
  } = useReportFoldersTableFilters();

  const onSearchChange = useMemo(
    () =>
      debounce(
        (val: ChangeEvent<HTMLInputElement>) => handleSearchChange(val),
        500,
      ),
    [handleSearchChange],
  );

  return (
    <Box
      sx={{
        width: '100%',
        padding: '1rem',
        display: 'flex',
        gap: TOOLBAR_FIELD_GAP,
        bgcolor: 'white',
        border: theme => `1px solid ${theme.palette.grey[300]}`,
        alignItems: 'center',
      }}
    >
      <OutlinedInput
        id='searchField'
        key={filters.search ?? ''}
        defaultValue={filters.search ?? ''}
        placeholder={t('reporting.folders.filters.search')}
        onChange={onSearchChange}
        sx={toolbarFieldSx}
        startAdornment={
          <InputAdornment sx={{ padding: 0 }} position='start'>
            <SearchIcon />
          </InputAdornment>
        }
      />
      {organizationOptions.length > 1 && (
        <Autocomplete
          sx={toolbarFieldSx}
          disablePortal
          options={organizationOptions}
          getOptionLabel={(option: { id: string; name: string }) => option.name}
          onChange={handleSelectOrg}
          value={org}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={t('reporting.report.filters.organization')}
              />
            );
          }}
        />
      )}
      {endSlot && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
          {endSlot}
        </Box>
      )}
    </Box>
  );
};
